// RepoPage.tsx — the repo's one page: three panes, one URL.
//
//   ┌──────────────┬──────────────────────────────────────────────┐
//   │ EXPERIMENTS  │  gitgraph (only this experiment's branches)  │
//   │ (tree, left) ├──────────────────────────────────────────────┤
//   │              │  COMMIT OVERVIEW — n selected                │
//   │              │  metrics table + live data feeds             │
//   │              ├──────────────────────────────────────────────┤
//   │              │  DIRTY MARKS — list, mark from the selection │
//   └──────────────┴──────────────────────────────────────────────┘
//
// This component owns no view state of its own: the selected experiment,
// the selected commits and the descendants toggle all live in the query
// string (?exp=…&sel=…,…&descendants=0), so a view is shareable by link and
// survives a reload. Param writes replace rather than push, so a session of
// clicking around the graph doesn't bury the page the user arrived from.
// Everything below reads those values as props and reports changes back up
// here, which is also why the graph and the overview pane never have to
// agree with each other about what is selected — there is one copy of that
// answer.
//
// The experiments list is fetched here because both the pane (to render the
// tree) and the graph (for the position-0 objective that headlines a row)
// need it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { unwrapJson, useAuth } from 'common';
import { apiClient, hasScope } from '../api';
import type { Experiment, ExperimentsResponse } from '../api';
import { hiddenBranches } from './closed';
import ExperimentsPane from './ExperimentsPane';
import RepoGraphView from './RepoGraphView';
import CommitOverviewPane from './CommitOverviewPane';
import DirtyMarksCard from './DirtyMarksCard';
import { PAGE_REFRESH_MS, useLiveTick } from './live';
import {
  descendantsParam, nextSelection, readViewState, selectionParam, writeViewState,
} from './viewState';

export default function RepoPage() {
  const { owner, name } = useParams<{ owner: string; name: string }>();
  const [search, setSearch] = useSearchParams();
  const auth = useAuth({ client: apiClient });
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloads, setReloads] = useState(0);
  // Set by an action that wants the reloaded list to land on a specific
  // experiment (a fork selects the child, a consolidate selects the parent).
  const [selectAfter, setSelectAfter] = useState<string | null>(null);
  // Bumped when a dirty mark is created, unmarked or deleted, so the graph
  // and the overview pane re-read their values' dirtiness.
  const [marksVersion, setMarksVersion] = useState(0);

  const view = readViewState(search);

  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setSearch(writeViewState(search, next), { replace: true });
    },
    [search, setSearch],
  );

  // Re-read everything once a minute (while the tab is visible). A
  // refresh is quiet: no "Loading…", and a failed one keeps what is shown.
  const minute = useLiveTick(true, PAGE_REFRESH_MS);
  const loadedKey = useRef('');

  useEffect(() => {
    if (!owner || !name) return;
    let cancelled = false;
    const key = `${owner}/${name}/${reloads}`;
    const quiet = loadedKey.current === key;
    if (!quiet) setLoading(true);
    (async () => {
      try {
        const res = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/experiments`);
        const body = await unwrapJson<ExperimentsResponse>(res);
        if (!cancelled) {
          setExperiments(body.experiments ?? []);
          setLoadError('');
          loadedKey.current = key;
        }
      } catch (err) {
        // A server without the experiments route yet leaves an empty pane
        // and a repo-wide graph, not a blank page.
        if (!cancelled && !quiet) {
          setExperiments([]);
          setLoadError(
            err instanceof Error ? err.message : 'Failed to load experiments',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, name, reloads, minute]);

  // Land on something. An action's follow-up selection wins (a fork selects
  // the child, a consolidate selects the parent); otherwise keep whatever the
  // URL named, and fall back to the first active experiment in lineage order
  // so the page is never showing three empty panes when there is data.
  useEffect(() => {
    if (loading || experiments.length === 0) return;
    const known = (slug: string) => experiments.some((e) => e.slug === slug);
    if (selectAfter !== null) {
      setSelectAfter(null);
      if (selectAfter && known(selectAfter)) {
        patch({ exp: selectAfter, sel: null });
        return;
      }
    } else if (view.experiment && known(view.experiment)) {
      return;
    }
    const pick = experiments.find((e) => e.status === 'active') ?? experiments[0];
    patch({ exp: pick.slug, sel: null });
    // view is derived from `search`, which `patch` rewrites; depending on the
    // whole object would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, experiments, selectAfter]);

  if (!owner || !name) return null;
  const repoPath = `${owner}/${name}`;

  const current = experiments.find((e) => e.slug === view.experiment) ?? null;
  const hasChildren = !!current && experiments.some((e) => e.parent_id === current.id);
  // Position order is the server's; objective=false metrics are tracked but
  // not optimized, so they fall behind the objectives.
  const objectives = (current?.objectives ?? [])
    .slice()
    .sort((a, b) => Number(b.objective) - Number(a.objective) || a.position - b.position)
    .map((o) => o.name);

  const selectCommit = (sha: string, additive: boolean) => {
    patch({ sel: selectionParam(nextSelection(view.selection, sha, additive)) });
  };

  return (
    <main>
      <div className="d-flex flex-wrap align-items-baseline gap-2">
        <h1 className="m-0">{repoPath}</h1>
        {current && (
          <span className="text-secondary">
            {current.title || current.slug}
            {view.descendants && hasChildren ? ' + descendants' : ''}
          </span>
        )}
      </div>
      <div className="repo-panes mt-3">
        <ExperimentsPane
          owner={owner}
          name={name}
          experiments={experiments}
          selected={view.experiment}
          onSelect={(slug) => {
            if (slug !== view.experiment) patch({ exp: slug, sel: null });
          }}
          descendants={view.descendants}
          onDescendantsChange={(on) => patch({ descendants: descendantsParam(on) })}
          showClosed={view.closed}
          onShowClosedChange={(on) => patch({ closed: on ? '1' : null })}
          canWrite={hasScope(auth.scopes, 'increment.use')}
          isAdmin={hasScope(auth.scopes, 'increment.admin')}
          loading={loading}
          loadError={loadError}
          onChanged={(select) => {
            setSelectAfter(select ?? null);
            setReloads((n) => n + 1);
          }}
        />
        <div className="repo-main">
          <RepoGraphView
            owner={owner}
            name={name}
            experiment={view.experiment}
            descendants={view.descendants}
            hiddenBranches={hiddenBranches(experiments, view.closed, view.experiment)}
            objectives={objectives}
            selection={view.selection}
            onSelect={selectCommit}
            reloadKey={marksVersion}
            refreshKey={minute}
            canCancelJobs={hasScope(auth.scopes, 'increment.use')}
          />
          <CommitOverviewPane
            owner={owner}
            name={name}
            experiment={view.experiment}
            descendants={view.descendants}
            objectives={objectives}
            selection={view.selection}
            onRemove={(sha) =>
              patch({ sel: selectionParam(view.selection.filter((s) => s !== sha)) })
            }
            onClear={() => patch({ sel: null })}
            reloadKey={marksVersion}
          />
          <DirtyMarksCard
            owner={owner}
            name={name}
            experiment={view.experiment}
            selection={view.selection}
            canWrite={hasScope(auth.scopes, 'increment.use')}
            isAdmin={hasScope(auth.scopes, 'increment.admin')}
            reloadKey={marksVersion + minute}
            onChanged={() => setMarksVersion((n) => n + 1)}
          />
        </div>
      </div>
    </main>
  );
}
